import { lazy } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { adminRoutes } from '@/features/admin/routes';
import { agentRoutes } from '@/features/agents/routes';
import { assetRoutes } from '@/features/assets/routes';
import { changeRoutes } from '@/features/changes/routes';
import { cmdbRoutes } from '@/features/cmdb/routes';
import { incidentRoutes } from '@/features/incidents/routes';
import { catalogRoutes } from '@/features/catalog/routes';
import { crmBranch } from '@/features/crm/routes-index';
import { knowledgeRoutes } from '@/features/knowledge/routes';
import { NotificationBell } from '@/features/notifications/bell';
import { notificationRoutes } from '@/features/notifications/routes';
import { profileRoutes } from '@/features/profile/routes';
import { reportRoutes } from '@/features/reports/routes';
import { problemRoutes } from '@/features/problems/routes';
import { SignInPage } from '@/features/auth/sign-in-page';
import { RedirectIfAuthed, RequireAuth } from '@/lib/auth/guards';
import { SessionGuard } from '@/lib/auth/session-guard';
import { ForbiddenPage } from './pages/forbidden';
import { UserMenu } from './layout/user-menu';
import { AppShell } from './layout/app-shell';
import { RootLayout } from './layout/root-layout';
import { NotFoundPage } from './pages/not-found';
import { RouteErrorPage } from './pages/route-error';

const DashboardPage = lazy(() => import('@/features/dashboard/dashboard-page').then((m) => ({ default: m.DashboardPage })));
const ChangePasswordPage = lazy(() => import('@/features/auth/change-password-page').then((m) => ({ default: m.ChangePasswordPage })));

/** Feature route arrays are spread here by later phases (incidents, crm, agents, …). */
/** CRM branch: later tasks append contacts, contracts and the CRM home index route. */
export const crmRoutes: RouteObject[] = [crmBranch];

export const featureRoutes: RouteObject[] = [...crmRoutes, ...agentRoutes, ...incidentRoutes, ...problemRoutes, ...changeRoutes, ...cmdbRoutes, ...assetRoutes, ...knowledgeRoutes, ...catalogRoutes, ...reportRoutes, ...notificationRoutes, ...profileRoutes, ...adminRoutes];

export const appRoutes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/sign-in', element: <RedirectIfAuthed><SignInPage /></RedirectIfAuthed> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/change-password', element: <ChangePasswordPage /> },
          {
            element: <SessionGuard />,
            children: [
              {
                path: '/',
                element: <AppShell navRight={<><NotificationBell /><UserMenu /></>} />,
                children: [
                  { index: true, element: <DashboardPage />, handle: { crumb: 'Dashboard' } },
                  { path: 'forbidden', element: <ForbiddenPage />, handle: { crumb: 'Forbidden' } },
                  ...featureRoutes,
                  { path: '*', element: <NotFoundPage />, handle: { crumb: 'Not found' } },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(appRoutes);
